/**
 * Cola de sincronización del entrenamiento y del diario de comidas.
 *
 * Una sola instancia de `SyncQueue` (de `@fitness/core`) sobre la tabla
 * `syncOps` de Dexie, con dos tipos de operación: `workout.sync` y `meal.sync`.
 * Cada gesto del usuario —completar una serie, apuntar una comida— encola una
 * operación pequeña y autosuficiente; antes de cada envío, {@link coalesceOps} y
 * {@link coalesceMealOps} funden las de su tipo en un único lote, así que una
 * hora de gimnasio o un día entero de comidas sin cobertura salen en una sola
 * petición por familia.
 *
 * La cola no tiene temporizador propio a propósito: `useSync` decide cuándo
 * llamar a `flushPending()` (al volver la red, al volver la app a primer plano
 * y, con 2 s de margen, después de cada `enqueue`).
 *
 * Hay una cola por cuenta, sobre la tabla `syncOps` de **su** base: quien
 * escribe en la base de una cuenta encola en la de esa misma cuenta (todas las
 * funciones de encolado reciben la base), y `flushPending()` sólo vacía la de
 * la cuenta activa. Al cerrar sesión, {@link stopSync} detiene el envío en
 * curso antes de que cambie el token: lo que no ha salido se queda en la base
 * de su cuenta y sale cuando ésta vuelva a entrar.
 */
import type {
  MealEntryUpsert,
  MealSyncRequest,
  MealSyncResponse,
  SetLogUpsert,
  WorkoutSessionUpsert,
  WorkoutSyncRequest,
  WorkoutSyncResponse,
} from '@fitness/api-client';
import { SyncQueue, type SyncOp } from '@fitness/core';

import { api, describeError } from '@/lib/api';
import { queryClient } from '@/lib/queryClient';
import { MAX_BATCH, WORKOUT_SYNC_KIND, coalesceOps } from './coalesce';
import {
  currentDb,
  type FitnessDB,
  type LocalMealEntry,
  type LocalSession,
  type LocalSet,
} from './db';
import { DexieSyncStorage } from './dexieSyncStorage';
import { WORKOUT_DELETE_KIND, deletePayload, stripSessionFromOps } from './discard';
import { MAX_MEAL_BATCH, MEAL_SYNC_KIND, coalesceMealOps } from './mealCoalesce';
import { AccountInactiveError, isPermanentError, isTransientError } from './syncErrors';

/** Margen tras un `enqueue` antes de intentar el envío. */
const DEBOUNCE_MS = 2000;

// --- Estado observable ----------------------------------------------------

/** Lo que la interfaz necesita saber de la cola, además de los pendientes. */
export interface SyncState {
  isSyncing: boolean;
  /** Último fallo de envío, ya traducido al español, o `null`. */
  lastError: string | null;
  /** Epoch ms del último envío correcto. */
  lastSyncAt: number | null;
}

const INITIAL_STATE: SyncState = { isSyncing: false, lastError: null, lastSyncAt: null };
let state: SyncState = INITIAL_STATE;
const listeners = new Set<() => void>();

function setState(patch: Partial<SyncState>): void {
  state = { ...state, ...patch };
  for (const listener of listeners) listener();
}

/** Instantánea estable para `useSyncExternalStore`. */
export function getSyncState(): SyncState {
  return state;
}

export function subscribeSyncState(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

// --- La cola --------------------------------------------------------------

// Se reexporta por compatibilidad: la clasificación vive en `syncErrors.ts`,
// que no toca Dexie y se puede probar con `node --test`.
export { isPermanentError, isTransientError };

/**
 * Anota en Dexie el resultado del lote: lo aceptado queda marcado con
 * `syncedAt` y lo rechazado con `syncError`. Un elemento rechazado **no** se
 * vuelve a encolar: el servidor lo ha considerado inválido y sólo la interfaz
 * puede decidir qué hacer con él.
 */
async function applySyncResult(
  db: FitnessDB,
  request: WorkoutSyncRequest,
  response: WorkoutSyncResponse,
): Promise<void> {
  const failed = new Map(response.errors.map((error) => [error.clientId, error.message]));
  const now = Date.now();

  await db.transaction('rw', db.sessions, db.sets, async () => {
    for (const session of request.sessions ?? []) {
      const message = failed.get(session.clientId);
      await db.sessions.update(
        session.clientId,
        message === undefined
          ? { syncedAt: now, syncError: null }
          : { syncError: message, syncedAt: null },
      );
    }
    for (const set of request.sets ?? []) {
      const message = failed.get(set.clientId);
      await db.sets.update(
        set.clientId,
        message === undefined
          ? { syncedAt: now, syncError: null }
          : { syncError: message, syncedAt: null },
      );
    }
  });
}

/**
 * Lo mismo para el diario: lo aceptado queda marcado con `syncedAt` y lo
 * rechazado con `syncError`. Un borrado aceptado se lleva por delante su
 * lápida, que ya no hace falta; uno rechazado la conserva para poder
 * reintentarlo a mano.
 */
async function applyMealSyncResult(
  db: FitnessDB,
  request: MealSyncRequest,
  response: MealSyncResponse,
): Promise<void> {
  const failed = new Map(response.errors.map((error) => [error.clientId, error.message]));
  const now = Date.now();

  await db.transaction('rw', db.mealEntries, async () => {
    for (const entry of request.entries ?? []) {
      const message = failed.get(entry.clientId);
      await db.mealEntries.update(
        entry.clientId,
        message === undefined
          ? { syncedAt: now, syncError: null }
          : { syncError: message, syncedAt: null },
      );
    }
    for (const clientId of request.deletedClientIds ?? []) {
      const message = failed.get(clientId);
      if (message === undefined) await db.mealEntries.delete(clientId);
      else await db.mealEntries.update(clientId, { syncError: message });
    }
  });
}

// --- Una cola por cuenta ---------------------------------------------------

/** La cola de una cuenta, atada a su base. */
interface AccountSync {
  db: FitnessDB;
  queue: SyncQueue;
  /** Pasa a `false` al cerrar sesión: desde ahí no sale nada más de esta cola. */
  active: boolean;
  /** Envíos en curso, para poder esperarlos al cerrar sesión. */
  inFlight: Set<Promise<void>>;
}

const syncs = new WeakMap<FitnessDB, AccountSync>();

/** ¿Puede enviar la cola? Sólo si es la de la cuenta activa y no se ha detenido. */
function isLive(sync: AccountSync): boolean {
  return sync.active && currentDb() === sync.db;
}

function createAccountSync(db: FitnessDB): AccountSync {
  const holder: { sync?: AccountSync } = {};
  const live = (): boolean => holder.sync !== undefined && isLive(holder.sync);
  // Última comprobación antes de cada petición: si la cuenta ya no está
  // activa la operación no sale y se queda, intacta, en la base de su cuenta.
  const check = (): void => {
    if (!live()) throw new AccountInactiveError();
  };
  const queue = new SyncQueue({
    storage: new DexieSyncStorage(db.syncOps),
    handlers: {
      [WORKOUT_SYNC_KIND]: async (op: SyncOp) => {
        const request = (op.payload ?? {}) as WorkoutSyncRequest;
        check();
        const response = await api.workouts.sync(request);
        await applySyncResult(db, request, response);
      },
      [MEAL_SYNC_KIND]: async (op: SyncOp) => {
        const request = (op.payload ?? {}) as MealSyncRequest;
        check();
        const response = await api.meals.sync(request);
        await applyMealSyncResult(db, request, response);
      },
      [WORKOUT_DELETE_KIND]: async (op: SyncOp) => {
        await handleSessionDeletion(db, check, op);
      },
    },
    isPermanentError,
    // Sin red no se gastan intentos: las series esperan en Dexie lo que haga falta.
    isTransientError,
    // Atada a su cuenta: en cuanto ésta cierra sesión no sale nada más.
    isActive: live,
    onDeadLetter: (op, error) => {
      if (live()) setState({ lastError: describeError(error) });
      void markDeadLetter(db, op);
    },
  });
  holder.sync = { db, queue, active: true, inFlight: new Set() };
  return holder.sync;
}

/** La cola de la base de una cuenta; se crea la primera vez que se pide. */
function syncFor(db: FitnessDB): AccountSync {
  let sync = syncs.get(db);
  if (sync === undefined) {
    sync = createAccountSync(db);
    syncs.set(db, sync);
  }
  return sync;
}

/**
 * Detiene la cola de la cuenta activa antes de cerrar su sesión: cancela el
 * envío programado y, desde este mismo instante, no deja salir ninguna
 * operación más. Devuelve una promesa que se resuelve cuando ha terminado lo
 * que estaba en vuelo.
 *
 * Hay que llamarla —y esperar su promesa— **antes** de que cambie el token,
 * para que nada de esta cuenta salga con el de la siguiente. Lo que no ha
 * salido se queda en su base, intacto, hasta que la cuenta vuelva.
 */
export function haltSync(): Promise<void> {
  cancelScheduledFlush();
  const db = currentDb();
  const sync = db === null ? undefined : syncs.get(db);
  if (sync !== undefined) sync.active = false;
  state = INITIAL_STATE;
  for (const listener of listeners) listener();
  if (sync === undefined) return Promise.resolve();
  return Promise.allSettled([...sync.inFlight]).then(() => undefined);
}

/**
 * Una operación descartada deja rastro en las filas que llevaba: si no,
 * desaparecerían del indicador de pendientes sin haber llegado nunca al
 * servidor y el usuario no se enteraría.
 */
async function markDeadLetter(db: FitnessDB, op: SyncOp): Promise<void> {
  const payload = op.payload;
  if (payload === null || typeof payload !== 'object') return;
  const message = op.lastError ?? 'No se ha podido sincronizar.';

  if (op.kind === MEAL_SYNC_KIND) {
    const meals = payload as MealSyncRequest;
    await db.transaction('rw', db.mealEntries, async () => {
      for (const entry of meals.entries ?? []) {
        await db.mealEntries.update(entry.clientId, { syncError: message });
      }
      for (const clientId of meals.deletedClientIds ?? []) {
        await db.mealEntries.update(clientId, { syncError: message });
      }
    });
    return;
  }

  const workout = payload as WorkoutSyncRequest;
  await db.transaction('rw', db.sessions, db.sets, async () => {
    for (const session of workout.sessions ?? []) {
      await db.sessions.update(session.clientId, { syncError: message });
    }
    for (const set of workout.sets ?? []) {
      await db.sets.update(set.clientId, { syncError: message });
    }
  });
}

// --- Encolado -------------------------------------------------------------

/** Traduce una sesión local a lo que espera el contrato. */
export function toSessionUpsert(session: LocalSession): WorkoutSessionUpsert {
  return {
    clientId: session.clientId,
    routineId: session.routineId,
    routineDayId: session.routineDayId,
    startedAt: session.startedAt,
    finishedAt: session.finishedAt,
    notes: session.notes,
  };
}

/** Traduce una serie local a lo que espera el contrato. */
export function toSetUpsert(set: LocalSet): SetLogUpsert {
  return {
    clientId: set.clientId,
    sessionClientId: set.sessionClientId,
    exerciseId: set.exerciseId,
    setNumber: set.setNumber,
    setType: set.setType,
    reps: set.reps,
    weightKg: set.weightKg,
    rpe: set.rpe,
    performedAt: set.performedAt,
    notes: set.notes,
    // Opcionales en el contrato: una serie guardada antes de que existieran
    // viaja con `null`, que es justo lo que significa «no se anotó».
    durationSeconds: set.durationSeconds ?? null,
    distanceM: set.distanceM ?? null,
  };
}

/**
 * Encola un lote. El identificador de la operación es el `clientId` del
 * elemento principal, así que reencolar la misma serie sustituye a la anterior
 * en vez de acumular basura.
 */
async function enqueue(db: FitnessDB, id: string, request: WorkoutSyncRequest): Promise<void> {
  await syncFor(db).queue.enqueue(WORKOUT_SYNC_KIND, request, id);
  scheduleFlush();
}

/** Encola la sesión (alta, cambio de notas o cierre) en la cola de la base `db`. */
export async function enqueueSession(db: FitnessDB, session: LocalSession): Promise<void> {
  await enqueue(db, session.clientId, { sessions: [toSessionUpsert(session)] });
}

/**
 * Encola una serie **junto con su sesión**. El servidor procesa las sesiones
 * antes que las series dentro del mismo lote, así que el lote se basta a sí
 * mismo aunque el alta de la sesión se hubiera perdido.
 */
export async function enqueueSet(
  db: FitnessDB,
  session: LocalSession,
  set: LocalSet,
): Promise<void> {
  await enqueue(db, set.clientId, {
    sessions: [toSessionUpsert(session)],
    sets: [toSetUpsert(set)],
  });
}

/** Encola el borrado de una serie que el servidor ya conoce. */
export async function enqueueSetDeletion(db: FitnessDB, setClientId: string): Promise<void> {
  await enqueue(db, setClientId, { deletedSetClientIds: [setClientId] });
}

// --- Descarte de sesiones -------------------------------------------------

/**
 * Quita de los lotes `workout.sync` pendientes la sesión y sus series, para que
 * no vuelva a subir después de descartarla. No cambia intentos ni esperas de
 * ninguna operación: sólo su contenido, y borra las que se quedan vacías.
 */
export async function dropSessionFromQueue(db: FitnessDB, sessionClientId: string): Promise<void> {
  await db.transaction('rw', db.syncOps, async () => {
    const { removeIds, updated } = stripSessionFromOps(await db.syncOps.toArray(), sessionClientId);
    if (removeIds.length > 0) await db.syncOps.bulkDelete(removeIds);
    if (updated.length > 0) await db.syncOps.bulkPut(updated);
  });
}

/**
 * Encola el borrado de una sesión en el servidor. Va en una operación propia
 * (`workout.delete`) con un id nuevo: si reutilizara el `clientId` de la
 * sesión, la cola podría confundirla con su alta y borrarla al confirmar ésta.
 */
export async function enqueueSessionDeletion(
  db: FitnessDB,
  sessionClientId: string,
): Promise<void> {
  await syncFor(db).queue.enqueue(WORKOUT_DELETE_KIND, { clientId: sessionClientId }, newOpId());
  scheduleFlush();
}

function newOpId(): string {
  return crypto.randomUUID();
}

/**
 * Handler de `workout.delete`. Antes de borrar vuelve a limpiar la cola: si un
 * envío que estaba en vuelo al descartar falló y se reprogramó, su lote aún
 * llevaría la sesión y la recrearía después del borrado. El `DELETE` es
 * idempotente (`204` aunque la sesión no exista), así que también vale para
 * una sesión que nunca llegó a subir.
 */
async function handleSessionDeletion(db: FitnessDB, check: () => void, op: SyncOp): Promise<void> {
  const payload = deletePayload(op.payload);
  if (payload === null) return;
  await dropSessionFromQueue(db, payload.clientId);
  check();
  await api.workouts.remove(payload.clientId);
}

// --- Encolado del diario de comidas ---------------------------------------

/**
 * Traduce una entrada local a lo que espera el contrato.
 *
 * Con `foodId` o `recipeId` sólo viaja la referencia y la cantidad: los macros
 * los calcula el servidor a partir de la ficha, y los que se guardaron aquí
 * eran únicamente para poder pintar la comida al instante. Sin ninguna de las
 * dos la entrada es manual y sí viajan sus valores, que el contrato no escala.
 *
 * `status` viaja siempre: el servidor trata una entrada sin él como comida, así
 * que corregir una planificada sin mandarlo la daría por comida. Las filas
 * guardadas antes de que existieran los planes no lo tienen y salen como
 * `eaten`, que es lo que eran.
 */
export function toMealUpsert(entry: LocalMealEntry): MealEntryUpsert {
  const base = {
    clientId: entry.clientId,
    date: entry.date,
    meal: entry.meal,
    quantity: entry.quantity,
    loggedAt: entry.loggedAt,
    notes: entry.notes,
    status: entry.status ?? 'eaten',
  } satisfies Partial<MealEntryUpsert>;
  if (entry.foodId !== null) return { ...base, foodId: entry.foodId };
  if (entry.recipeId !== null) return { ...base, recipeId: entry.recipeId };
  return {
    ...base,
    manual: {
      label: entry.label,
      kcal: entry.totals.kcal,
      proteinG: entry.totals.proteinG,
      carbsG: entry.totals.carbsG,
      fatG: entry.totals.fatG,
    },
  };
}

/** Encola un lote del diario, con el `clientId` de la entrada como id. */
async function enqueueMeal(db: FitnessDB, id: string, request: MealSyncRequest): Promise<void> {
  await syncFor(db).queue.enqueue(MEAL_SYNC_KIND, request, id);
  scheduleFlush();
}

/** Encola el alta o la corrección de una entrada del diario. */
export async function enqueueMealEntry(db: FitnessDB, entry: LocalMealEntry): Promise<void> {
  await enqueueMeal(db, entry.clientId, { entries: [toMealUpsert(entry)] });
}

/** Encola el borrado de una entrada. Es idempotente en el servidor. */
export async function enqueueMealDeletion(db: FitnessDB, clientId: string): Promise<void> {
  await enqueueMeal(db, clientId, { deletedClientIds: [clientId] });
}

// --- Envío ----------------------------------------------------------------

/**
 * Funde las operaciones pendientes en una sola antes de enviarlas.
 *
 * Sólo se tocan las que ya les toca salir: fusionar una operación en espera de
 * reintento le borraría el backoff y la cola se pondría a machacar al
 * servidor. Las que ya fallaron alguna vez también entran (una hora sin
 * cobertura deja decenas): la fusionada hereda sus intentos, así que el
 * backoff y la cuenta para el descarte siguen donde estaban.
 */
export async function coalescePending(db: FitnessDB): Promise<void> {
  const now = Date.now();
  await db.transaction('rw', db.syncOps, async () => {
    const all = await db.syncOps.toArray();
    const due = all.filter((op) => op.nextAttemptAt <= now);

    const merged = (
      kind: string,
      result: { request: unknown; consumedIds: string[]; createdAt: number },
    ): SyncOp => {
      const consumed = due.filter((op) => result.consumedIds.includes(op.id));
      return {
        id: crypto.randomUUID(),
        kind,
        payload: result.request,
        createdAt: result.createdAt,
        attempts: Math.max(0, ...consumed.map((op) => op.attempts)),
        failures: Math.max(0, ...consumed.map((op) => op.failures ?? 0)),
        nextAttemptAt: now,
      };
    };

    const workouts = coalesceOps(due, MAX_BATCH);
    if (workouts !== null) {
      await db.syncOps.bulkDelete(workouts.consumedIds);
      await db.syncOps.put(merged(WORKOUT_SYNC_KIND, workouts));
    }

    const meals = coalesceMealOps(due, MAX_MEAL_BATCH);
    if (meals !== null) {
      await db.syncOps.bulkDelete(meals.consumedIds);
      await db.syncOps.put(merged(MEAL_SYNC_KIND, meals));
    }
  });
}

/** Adelanta los reintentos en espera: hay motivos para creer que ya hay red. */
async function expediteRetries(db: FitnessDB): Promise<number> {
  const now = Date.now();
  return db.syncOps
    .filter((op) => op.nextAttemptAt > now)
    .modify((op) => {
      op.nextAttemptAt = now;
    });
}

/**
 * Vuelve a encolar lo que quedó marcado con `syncError`: lo que el servidor
 * rechazó y lo que se descartó tras agotar los intentos (por ejemplo, las
 * series de una sesión entera sin cobertura con la versión anterior de la
 * cola). Sin esto, esas filas se quedaban en el dispositivo para siempre sin
 * llegar nunca al servidor.
 *
 * Es idempotente en el servidor (todo va por `clientId`); si el rechazo era
 * legítimo, el servidor lo vuelve a rechazar y la fila vuelve a quedar
 * marcada. Devuelve cuántas filas se han vuelto a encolar.
 */
export async function requeueRejected(): Promise<number> {
  const db = currentDb();
  if (db === null) return 0;
  const [sessions, sets] = await Promise.all([
    db.sessions.filter((row) => row.syncError !== null).toArray(),
    db.sets.filter((row) => row.syncError !== null && row.syncedAt === null).toArray(),
  ]);
  if (sessions.length === 0 && sets.length === 0) return 0;

  let count = 0;
  for (const session of sessions) {
    const clean: LocalSession = { ...session, syncError: null };
    await db.sessions.update(session.clientId, { syncError: null });
    await enqueueSession(db, clean);
    count += 1;
  }
  for (const set of sets) {
    const session = await db.sessions.get(set.sessionClientId);
    // Sin su sesión el servidor no sabría dónde colgarla; la fila se queda
    // marcada y a la vista.
    if (!session) continue;
    const clean: LocalSet = { ...set, syncError: null };
    await db.sets.update(set.clientId, { syncError: null });
    await enqueueSet(db, { ...session, syncError: null }, clean);
    count += 1;
  }
  return count;
}

/** Número de operaciones pendientes de enviar, de un tipo o de todos. */
export async function pendingCount(kind?: string): Promise<number> {
  const db = currentDb();
  if (db === null) return 0;
  if (kind === undefined) return db.syncOps.count();
  return db.syncOps.filter((op) => op.kind === kind).count();
}

/**
 * Cambios de la cuenta activa que aún no han llegado al servidor, contados
 * como los ve el usuario: sesiones, series y entradas del diario sin subir.
 * Si sólo quedan operaciones sueltas (un borrado), cuenta ésas. Es el número
 * del aviso al cerrar sesión.
 */
export async function pendingChanges(): Promise<number> {
  const db = currentDb();
  if (db === null) return 0;
  const [sessions, sets, meals, ops] = await Promise.all([
    db.sessions.filter((row) => row.syncedAt === null).count(),
    db.sets.filter((row) => row.syncedAt === null).count(),
    db.mealEntries.filter((row) => row.syncedAt === null).count(),
    db.syncOps.count(),
  ]);
  const rows = sessions + sets + meals;
  return rows > 0 ? rows : ops;
}

/**
 * Funde, envía y refresca las consultas que dependen del servidor. Es seguro
 * llamarla desde varios sitios a la vez: `SyncQueue.flush()` es *single-flight*.
 *
 * Vacía la cola entera —entrenamiento y comidas— porque el coste de una pasada
 * es el mismo y así nunca queda media cola sin subir por haber entrado por la
 * pantalla «equivocada».
 *
 * Con `force` no se respeta el backoff: se usa cuando hay motivos para creer
 * que la red ha vuelto (evento `online`, la app vuelve a primer plano, el
 * botón «Sincronizar ahora»). `navigator.onLine` no basta: en un sótano sin
 * cobertura el móvil sigue diciendo que hay red.
 */
export function flushPending(options: { force?: boolean } = {}): Promise<void> {
  const db = currentDb();
  if (db === null) return Promise.resolve();
  const sync = syncFor(db);
  if (!isLive(sync)) return Promise.resolve();
  const running = runFlush(sync, options);
  sync.inFlight.add(running);
  void running.finally(() => sync.inFlight.delete(running));
  return running;
}

async function runFlush(sync: AccountSync, options: { force?: boolean }): Promise<void> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
  const { db, queue } = sync;
  setState({ isSyncing: true });
  try {
    if (options.force === true) await expediteRetries(db);
    await coalescePending(db);
    let result = await queue.flush();
    // Si algo ha llegado, hay red: lo que esperaba su turno de reintento sale
    // ya, en vez de quedarse hasta cinco minutos más en el móvil.
    if (result.succeeded > 0 && isLive(sync) && (await expediteRetries(db)) > 0) {
      await coalescePending(db);
      const again = await queue.flush();
      result = {
        processed: result.processed + again.processed,
        succeeded: result.succeeded + again.succeeded,
        failed: result.failed + again.failed,
        deferred: again.deferred,
      };
    }
    // La cuenta ha cerrado sesión mientras tanto: su estado ya no lo mira nadie.
    if (!isLive(sync)) return;
    if (result.succeeded > 0) {
      setState({ lastError: null, lastSyncAt: Date.now() });
      await queryClient.invalidateQueries({ queryKey: ['workouts'] });
      await queryClient.invalidateQueries({ queryKey: ['progress'] });
      await queryClient.invalidateQueries({ queryKey: ['meals'] });
      await queryClient.invalidateQueries({ queryKey: ['nutrition'] });
    }
    if (result.deferred > 0 && state.lastError === null) {
      setState({
        lastError: 'Sin conexión con el servidor: está guardado en el móvil y se subirá solo.',
      });
    }
  } catch (error) {
    if (isLive(sync)) setState({ lastError: describeError(error) });
  } finally {
    if (isLive(sync)) setState({ isSyncing: false });
  }
}

let timer: ReturnType<typeof setTimeout> | null = null;

/** Envío diferido tras un `enqueue`: agrupa la ráfaga de series de una serie. */
export function scheduleFlush(delayMs = DEBOUNCE_MS): void {
  if (typeof window === 'undefined') return;
  if (navigator.onLine === false) return;
  if (timer !== null) clearTimeout(timer);
  timer = setTimeout(() => {
    timer = null;
    void flushPending();
  }, delayMs);
}

/** Cancela un envío diferido pendiente (al desmontar la app, en tests). */
export function cancelScheduledFlush(): void {
  if (timer === null) return;
  clearTimeout(timer);
  timer = null;
}
